import React, { useEffect, useRef, useState } from 'react';
import { useGlobalState } from '../../../state/GlobalStateProvider';
import './TextEditor.scss';

const TextEditor = ({
  maxCharacters = 250,
  handleAttachments,
  handleContent,
  refreshData,
  disabled,
  disableAttachments = false,
}) => {
  const [{ protocolMapping }] = useGlobalState();
  const [editorHTML, setEditorHTML] = useState('');
  const [showProtocolMenu, setShowProtocolMenu] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [charCount, setCharCount] = useState(0);
  const editorRef = useRef(null);

  const updateEditorHTML = () => {
    if (editorRef.current) {
      let text = editorRef.current.textContent || '';
      if (text.length > maxCharacters) {
        text = text.substring(0, maxCharacters);
        editorRef.current.textContent = text;
      }
      setCharCount(text.length);
      setEditorHTML(editorRef.current.innerHTML);
      handleContent(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    updateEditorHTML();
  };

  const handleKeyDown = (e) => {
    const textLength = editorRef.current?.textContent.length || 0;
    if (e.key.length === 1 && textLength >= maxCharacters) {
      e.preventDefault();
      return;
    }
    // if (e.key === '/') {
    //   setShowProtocolMenu(true);
    // }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = (e.clipboardData || window.clipboardData).getData('text');
    const currentText = editorRef.current?.textContent || '';
    const spaceLeft = maxCharacters - currentText.length;

    if (spaceLeft <= 0) return;

    const textToInsert = pastedText.substring(0, spaceLeft);
    insertTextAtCursor(textToInsert);
    updateEditorHTML();
  };

  const insertTextAtCursor = (text) => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const textNode = document.createTextNode(text);
    range.insertNode(textNode);

    // Move cursor after inserted text
    range.setStartAfter(textNode);
    range.setEndAfter(textNode);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const handleBold = () => {
    document.execCommand('bold');
    updateEditorHTML();
  };

  const handleItalic = () => {
    document.execCommand('italic');
    updateEditorHTML();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));

    const currentImages = attachments.length;
    const allowedImages = Math.max(0, 4 - currentImages);

    const filesToAdd = imageFiles.slice(0, allowedImages);
    if (filesToAdd.length < imageFiles.length) {
      alert.error('You can only upload up to 4 images.');
    }

    handleAttachments((prev) => [...prev, ...filesToAdd]);

    const newAttachments = [];
    filesToAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        newAttachments.push(event.target.result);
        if (newAttachments.length === filesToAdd.length) {
          setAttachments((prev) => [...prev, ...newAttachments]);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // const insertProtocol = (protocol) => {
  //   if (!editorRef.current) return;

  //   const span = document.createElement('span');
  //   span.textContent = `/${protocol?.identifier} `;
  //   editorRef.current.appendChild(span);
  //   setShowProtocolMenu(false);
  //   updateEditorHTML();
  // };

  useEffect(() => {
    if (refreshData) {
      setAttachments([]);
      setEditorHTML('');
      setCharCount(0);
      if (editorRef.current) editorRef.current.innerHTML = '';
    }
  }, [refreshData]);

  return (
    <div className='text-editor-container'>
      <div className='toolbar'>
        <button onClick={handleBold} disabled={disabled}>
          <b>B</b>
        </button>
        <button onClick={handleItalic} disabled={disabled}>
          <i>I</i>
        </button>
        <label>
          <input
            disabled={disabled || disableAttachments}
            type='file'
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          📎
        </label>
        <div className='char-count'>
          {charCount} / {maxCharacters}
        </div>
      </div>

      <div
        className='editor'
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        onDrop={(e) => e.preventDefault()}
        
      />

      {/* {showProtocolMenu && (
        <div className='protocol-menu'>
          {Object.values(protocolMapping || {}).map((p, idx) => (
            <div key={idx} onClick={() => insertProtocol(p)}>
              {p.name}
            </div>
          ))}
        </div>
      )} */}

      <div className='attachments-preview'>
        {attachments.map((preview, idx) => (
          <div className='attachment-item' key={idx}>
            <img src={preview} alt={`attachment-${idx}`} />
            <button className='remove-btn' onClick={() => handleRemoveAttachment(idx)}>
              X
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TextEditor;
